import React, { useState } from "react";
import { Button, Container, Tab, Table, Tabs } from "react-bootstrap";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-select";
import { useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { formatDate, formatDateFromAPI } from "../helpers/functions";
import MyGraph from "./MyGraph";
import { useSelector } from "react-redux";
import { selectUser } from "../store/userSlice";

const commsOptions = [
  { value: "sms", label: "Sms" },
  { value: "email", label: "Email" },
  { value: "voice", label: "Voice" },
  { value: "threaded", label: "Threaded" },
  { value: "inbound", label: "Inbound" },
];
const statusesOptions = [
  { value: "A", label: "Accepted" },
  { value: "B", label: "Busy" },
  { value: "C", label: "Cancelled" },
  { value: "D", label: "Delivered" },
  { value: "E", label: "Expired" },
  { value: "F", label: "Failed" },
  { value: "H", label: "Hung up" },
  { value: "I", label: "Ignored" },
  { value: "L", label: "Rate Limited" },
  { value: "P", label: "No Pending" },
  { value: "R", label: "Retrying" },
  { value: "Q", label: "Queued" },
  { value: "S", label: "Sent" },
  { value: "U", label: "Undeliverable" },
  { value: "X", label: "Payment required" },
];

export default function Data() {
  const user = useSelector(selectUser);
  let [searchParams, setSearchParams] = useSearchParams();

  // pagination in search params
  const limit = parseInt(searchParams.get("limit") || 10);
  const offset = parseInt(searchParams.get("offset") || 0);
  // order
  const order = searchParams.get("order") || "desc";
  const orderBy = searchParams.get("orderBy") || "records";
  // filters in search params
  const dbRef = searchParams.get("dbRef") || "";
  const countries = searchParams.getAll("countries");
  const accounts = searchParams.getAll("accounts");
  const comms = searchParams.getAll("comms");
  const customers = searchParams.getAll("customers");
  const parents = searchParams.getAll("parents");
  const status = searchParams.getAll("status");

  const today = new Date(new Date(new Date().setHours(0)).setMinutes(0));
  const startDate = new Date(searchParams.get("startDate") || today);
  const endDate = new Date(
    searchParams.get("endDate") || new Date(today.setDate(today.getDate() + 1))
  );
  const tab = searchParams.get("tab") || "summary";

  const [dbInfo, setDbInfo] = useState(null);

  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState([]);
  const [dbRefOptions, setDbRefOptions] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [accountOptions, setAccountOptions] = useState([]);
  const [countriesOptions, setCountriesOptions] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();

  const getRecords = async () => {
    let headersList = {
      Accept: "*/*",
    };

    let response = await fetch(
      `/auth/records?db_ref=${dbRef}${accounts
        .map((a) => `&account_id=${a}`)
        .join("")}${parents.map((a) => `&parent_id=${a}`).join("")}${customers
        .map((a) => `&customer_id=${a}`)
        .join("")}${comms.map((c) => `&kind=${c}`).join("")}${countries
        .map((c) => `&mcc=${c}`)
        .join("")}&start_date=${formatDate(startDate)}&end_date=${formatDate(
        endDate
      )}${status
        .map((s) => `&status=${s}`)
        .join(
          ""
        )}&order=${order}&order_by=${orderBy}&limit=${limit}&offset=${offset}`,
      {
        method: "GET",
        headers: headersList,
      }
    );

    let data = await response.json();
    if (response.ok) {
      setRecords(data.data);
      setTotal(data.total);
    } else {
      toast.error(data.error);
      if (response.status === 401) {
        if (user) {
          toast.error("Your session has expired. You need to login again.");
        }
        navigate("/login", { state: { from: location } });
      }
    }
  };

  const getDbInfo = async () => {
    let headersList = {
      Accept: "*/*",
    };

    let response = await fetch("/auth/db-info", {
      method: "GET",
      headers: headersList,
    });

    let data = await response.json();
    if (response.ok) {
      setDbRefOptions(
        data.map((d) => ({
          value: d.DbRef,
          label: d.DbRef,
          data: d,
        }))
      );
      if (data.length >= 1) {
        let db;
        if (dbRef) {
          db = data.find((d) => d.DbRef === dbRef);
        } else {
          db = data[0];
          searchParams.set("dbRef", db?.DbRef);
          setSearchParams(searchParams, { replace: true });
        }
        if (!db) {
          toast.error("Database not found");
          return;
        }
        setDbInfo(db);
        setAccountOptions(
          db?.Accounts?.map((a) => ({ value: a.Id, label: a.Label })) ?? []
        );
        setCustomerOptions(
          db?.Customers?.map((a) => ({
            value: a.Id,
            label: a.Label,
            customer: a,
          })) ?? []
        );
        setCountriesOptions(
          db?.Countries.map((c) => ({
            value: c.Id,
            label: c.Label,
          }))
        );
      }
    } else {
      toast.error(data.error);
      if (response.status === 401) {
        if (user) {
          toast.error("Your session has expired. You need to login again.");
        }
        navigate("/login", { state: { from: location } });
      }
    }
  };

  const getSummary = async () => {
    let headersList = {
      Accept: "*/*",
    };

    let response = await fetch(
      `/auth/records/summary?db_ref=${dbRef}${accounts
        .map((a) => `&account_id=${a}`)
        .join("")}${parents.map((a) => `&parent_id=${a}`).join("")}${customers
        .map((a) => `&customer_id=${a}`)
        .join("")}${comms.map((c) => `&kind=${c}`).join("")}${countries
        .map((c) => `&mcc=${c}`)
        .join("")}&start_date=${formatDate(startDate)}&end_date=${formatDate(
        endDate
      )}${status
        .map((s) => `&status=${s}`)
        .join(
          ""
        )}&order=${order}&order_by=${orderBy}&limit=${limit}&offset=${offset}`,
      {
        method: "GET",
        headers: headersList,
      }
    );

    let data = await response.json();
    if (response.ok) {
      setSummary(data.data);
      setTotal(data.total);
    } else {
      toast.error(data.error);
      if (response.status === 401) {
        if (user) {
          toast.error("Your session has expired. You need to login again.");
        }
        navigate("/login", { state: { from: location } });
      }
    }
  };

  useEffect(() => {
    if (dbRef && tab === "details") {
      getRecords();
    }
    if (dbRef && tab === "summary") {
      getSummary();
    }
    if (!dbInfo || !dbRef) {
      getDbInfo();
    }
  }, [location]);

  return (
    <Container fluid>
      <div>
        <MyGraph page="records" title="Data" />
      </div>
      <div>
        <div className="my-2 row g-3 align-items-end">
          <div className="col-12 col-md-6 col-xl-3">
            <div>Db ref</div>
            <Select
              value={dbRefOptions.find((o) => o.value === dbRef)}
              onChange={(o) => {
                searchParams.set("dbRef", o?.value ?? "");
                setSearchParams(searchParams);
                setDbInfo(o.data);
                setAccountOptions(
                  o?.data?.Accounts?.map((a) => ({
                    value: a.Id,
                    label: a.Label,
                  })) ?? []
                );
                setCustomerOptions(
                  o?.data?.Customers?.map((a) => ({
                    value: a.Id,
                    label: a.Label,
                    customer: a,
                  })) ?? []
                );
                setCountriesOptions(
                  o?.data?.Countries.map((c) => ({
                    value: c.Id,
                    label: c.Label,
                  })) ?? []
                );
              }}
              options={dbRefOptions}
              maxMenuHeight={150}
            />
          </div>

          <div className="col-12 col-md-6 col-xl-3">
            <div>Comms</div>
            <Select
              value={commsOptions.filter((c) => comms.includes(c.value))}
              onChange={(values) => {
                searchParams.delete("comms");
                values.forEach((v) => searchParams.append("comms", v.value));
                setSearchParams(searchParams);
              }}
              isMulti
              options={commsOptions}
              maxMenuHeight={150}
            />
          </div>

          <div className="col-12 col-md-6 col-xl-3">
            <div>Countries</div>
            <Select
              value={countriesOptions.filter((o) =>
                countries.includes(o.value.toString())
              )}
              onChange={(values) => {
                searchParams.delete("countries");
                values.forEach((v) =>
                  searchParams.append("countries", v.value)
                );
                setSearchParams(searchParams);
              }}
              isMulti
              options={countriesOptions}
              maxMenuHeight={150}
            />
          </div>

          <div className="col-12 col-md-6 col-xl-3">
            <div>Account</div>
            <Select
              value={accountOptions.filter((o) =>
                accounts.includes(o.value.toString())
              )}
              onChange={(values) => {
                searchParams.delete("accounts");
                values.forEach((v) => searchParams.append("accounts", v.value));
                setSearchParams(searchParams);
              }}
              isMulti
              options={accountOptions}
              maxMenuHeight={150}
            />
          </div>

          <div className="col-12 col-md-6 col-xl-3">
            <div>Customers</div>
            <Select
              value={customerOptions.filter((o) =>
                customers.includes(o.value.toString())
              )}
              onChange={(values) => {
                searchParams.delete("customers");
                values.forEach((v) =>
                  searchParams.append("customers", v.value)
                );
                setSearchParams(searchParams);
              }}
              isMulti
              options={customerOptions}
              maxMenuHeight={150}
            />
          </div>

          <div className="col-12 col-md-6 col-xl-3">
            <div>Parent Customers</div>
            <Select
              value={customerOptions
                .filter((c) => c.customer.IsParent)
                .filter((o) => parents.includes(o.value.toString()))}
              onChange={(values) => {
                searchParams.delete("parents");
                values.forEach((v) => searchParams.append("parents", v.value));
                setSearchParams(searchParams);
              }}
              isMulti
              options={customerOptions.filter((c) => c.customer.IsParent)}
              maxMenuHeight={150}
            />
          </div>

          <div className="col-12 col-md-6 col-xl-3">
            <div>Start Date</div>
            <DatePicker
              showTimeSelect
              selected={startDate}
              onChange={(date) => {
                searchParams.set("startDate", date.toISOString());
                setSearchParams(searchParams);
              }}
              className="form-control"
              dateFormat={"dd-MM-yyyy HH:mm"}
            />
          </div>

          <div className="col-12 col-md-6 col-xl-3">
            <div>End Date</div>
            <DatePicker
              showTimeSelect
              selected={endDate}
              onChange={(date) => {
                searchParams.set("endDate", date.toISOString());
                setSearchParams(searchParams);
              }}
              className="form-control"
              dateFormat={"dd-MM-yyyy HH:mm"}
            />
          </div>

          <div className="col-12 col-md-6 col-xl-3">
            <div>Status</div>
            <Select
              value={statusesOptions.filter((o) =>
                status.includes(o.value.toString())
              )}
              onChange={(values) => {
                searchParams.delete("status");
                values.forEach((v) => searchParams.append("status", v.value));
                setSearchParams(searchParams);
              }}
              isMulti
              options={statusesOptions}
              maxMenuHeight={150}
            />
          </div>

          {/* <div className="col-6 col-md-3 ms-auto text-end"></div> */}

          <div className="col-12 col-md-6 col-xl-3 ms-auto text-end">
            <Button
              className="btn-secondary btn-sm me-4"
              onClick={() => {
                getRecords();
              }}
            >
              Refresh
            </Button>
            <Button
              className="btn-secondary ms-4 btn-sm"
              onClick={() => {
                setSearchParams(new URLSearchParams({ dbRef }));
              }}
            >
              Reset
            </Button>
          </div>
        </div>
        <Tabs
          id="controlled-tab-example"
          activeKey={tab}
          onSelect={(k) => {
            if (k === "summary") {
              searchParams.set("orderBy", "records");
              searchParams.set("order", "desc");
            } else {
              searchParams.set("orderBy", "date_at");
              searchParams.set("order", "desc");
            }
            searchParams.set("tab", k);
            searchParams.set("offset", 0);
            setSearchParams(searchParams);
          }}
          className="my-5"
        >
          <Tab eventKey="summary" title="Summary">
            <Table striped bordered hover className="mt-4">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th> Account</th>
                  <th>Kind</th>
                  <th>Country</th>
                  <th>Status</th>
                  <th>
                    <button
                      className="btn p-0 fw-bold text-nowrap"
                      onClick={() => {
                        if (orderBy === "records") {
                          if (order === "asc") {
                            searchParams.set("order", "desc");
                          } else {
                            searchParams.set("order", "asc");
                          }
                        } else {
                          searchParams.set("order", "desc");
                          searchParams.set("orderBy", "records");
                        }
                        setSearchParams(searchParams);
                      }}
                    >
                      Records{" "}
                      {orderBy === "records" ? (
                        order === "desc" ? (
                          <FontAwesomeIcon icon={faChevronDown} />
                        ) : (
                          <FontAwesomeIcon icon={faChevronUp} />
                        )
                      ) : (
                        ""
                      )}
                    </button>
                  </th>
                  <th>
                    <button
                      className="btn p-0 fw-bold text-nowrap"
                      onClick={() => {
                        if (orderBy === "items") {
                          if (order === "asc") {
                            searchParams.set("order", "desc");
                          } else {
                            searchParams.set("order", "asc");
                          }
                        } else {
                          searchParams.set("order", "desc");
                          searchParams.set("orderBy", "items");
                        }
                        setSearchParams(searchParams);
                      }}
                    >
                      Transactions{" "}
                      {orderBy === "items" ? (
                        order === "desc" ? (
                          <FontAwesomeIcon icon={faChevronDown} />
                        ) : (
                          <FontAwesomeIcon icon={faChevronUp} />
                        )
                      ) : (
                        ""
                      )}
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {summary.map((record) => (
                  <tr>
                    <td>{record.Customer}</td>
                    <td>{record.Account}</td>
                    <td>{record.Kind}</td>
                    <td>{record.Country}</td>
                    <td>{record.Status} </td>
                    <td>{record.Records} </td>
                    <td>{record.Items} </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Tab>
          <Tab eventKey="details" title="Details">
            <Table striped bordered hover className="mt-4">
              <thead>
                <tr>
                  <th>
                    <button
                      className="btn p-0 fw-bold text-nowrap"
                      onClick={() => {
                        if (orderBy === "date_at") {
                          if (order === "asc") {
                            searchParams.set("order", "desc");
                          } else {
                            searchParams.set("order", "asc");
                          }
                        } else {
                          searchParams.set("order", "desc");
                          searchParams.set("orderBy", "date_at");
                        }
                        setSearchParams(searchParams);
                      }}
                    >
                      Date{" "}
                      {orderBy === "date_at" ? (
                        order === "desc" ? (
                          <FontAwesomeIcon icon={faChevronDown} />
                        ) : (
                          <FontAwesomeIcon icon={faChevronUp} />
                        )
                      ) : (
                        ""
                      )}
                    </button>
                  </th>
                  <th>Comms</th>
                  <th> Account</th>
                  <th>Customer</th>
                  <th>Country</th>
                  <th>
                    <button
                      className="btn p-0 fw-bold text-nowrap"
                      onClick={() => {
                        if (orderBy === "records") {
                          if (order === "asc") {
                            searchParams.set("order", "desc");
                          } else {
                            searchParams.set("order", "asc");
                          }
                        } else {
                          searchParams.set("order", "desc");
                          searchParams.set("orderBy", "records");
                        }
                        setSearchParams(searchParams);
                      }}
                    >
                      Records{" "}
                      {orderBy === "records" ? (
                        order === "desc" ? (
                          <FontAwesomeIcon icon={faChevronDown} />
                        ) : (
                          <FontAwesomeIcon icon={faChevronUp} />
                        )
                      ) : (
                        ""
                      )}
                    </button>
                  </th>
                  <th>
                    <button
                      className="btn p-0 fw-bold text-nowrap"
                      onClick={() => {
                        if (orderBy === "items") {
                          if (order === "asc") {
                            searchParams.set("order", "desc");
                          } else {
                            searchParams.set("order", "asc");
                          }
                        } else {
                          searchParams.set("order", "desc");
                          searchParams.set("orderBy", "items");
                        }
                        setSearchParams(searchParams);
                      }}
                    >
                      Transactions{" "}
                      {orderBy === "items" ? (
                        order === "desc" ? (
                          <FontAwesomeIcon icon={faChevronDown} />
                        ) : (
                          <FontAwesomeIcon icon={faChevronUp} />
                        )
                      ) : (
                        ""
                      )}
                    </button>
                  </th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {records.map((record) => (
                  <tr>
                    <td>{formatDateFromAPI(record.DateAt)}</td>
                    <td>{record.Kind}</td>
                    <td>{record.Account}</td>
                    <td>{record.Customer}</td>
                    <td>{record.Country}</td>
                    <td>{record.Records} </td>
                    <td>{record.Items} </td>
                    <td>{record.Status} </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <p className="my-4">
              Records in this database are in{" "}
              <strong>{dbInfo?.TimeZone}</strong> time zone
            </p>
          </Tab>
        </Tabs>

        <div className="d-flex justify-content-between  align-items-center mt-5 gap-3 ">
          <div>
            <span>{total} records found</span>
          </div>
          <div>
            <Button
              disabled={offset === 0}
              variant="secondary"
              className="btn-sm me-3"
              onClick={() => {
                searchParams.set("offset", offset - limit);
                setSearchParams(searchParams);
              }}
            >
              Previous
            </Button>
            <span className="fw-bold"> Page {offset / limit + 1} </span>
            <Button
              disabled={offset + limit >= total}
              variant="secondary"
              className="btn-sm ms-3"
              onClick={() => {
                searchParams.set("offset", offset + limit);
                setSearchParams(searchParams);
              }}
            >
              Next
            </Button>
          </div>
          <div>
            Show
            <select
              className="btn"
              value={limit}
              onChange={(e) => {
                searchParams.set("offset", 0);
                searchParams.set("limit", e.target.value);
                setSearchParams(searchParams);
              }}
            >
              <option value={2}>2</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            records per page.
          </div>
        </div>
      </div>
    </Container>
  );
}
